<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
  categoryData: { type: Object, default: () => ({}) },
  vehicles: { type: Array, default: () => [] },
  initialVisibleRows: { type: Number, default: 5 },
  title: { type: String, default: '' },
});
const ICONS = ['available', 'disable', 'standard', 'option'];
const visibleRows = ref(props.initialVisibleRows);

const selectedLeft = ref(null);
const selectedMiddle = ref(null);
const selectedRight = ref(null);

const totalVehicles = computed(() =>
  Array.isArray(props.vehicles) ? props.vehicles : [],
);
const vCount = computed(() => totalVehicles.value.length);

const showSelectors = computed(() => vCount.value > 3);

const hasLeft = computed(() => vCount.value >= 1);
const hasRight = computed(() => vCount.value >= 2);
const hasMiddle = computed(() => vCount.value >= 3);

watch(
  () => props.vehicles,
  (newVehicles) => {
    const v = Array.isArray(newVehicles) ? newVehicles : [];
    selectedLeft.value = v[0] || null;
    selectedRight.value = v[1] || null;
    selectedMiddle.value = v[2] || null;
  },
  { immediate: true, deep: true },
);

const leftOptions = computed(() =>
  totalVehicles.value.filter(
    (v) =>
      v.id !== selectedMiddle.value?.id && v.id !== selectedRight.value?.id,
  ),
);
const middleOptions = computed(() =>
  totalVehicles.value.filter(
    (v) => v.id !== selectedLeft.value?.id && v.id !== selectedRight.value?.id,
  ),
);
const rightOptions = computed(() =>
  totalVehicles.value.filter(
    (v) => v.id !== selectedLeft.value?.id && v.id !== selectedMiddle.value?.id,
  ),
);

const optionsAll = computed(() => props.categoryData?.options || []);
const optionsVisible = computed(() =>
  optionsAll.value.slice(0, visibleRows.value),
);

function normalize(val) {
  if (val === null || val === undefined) return [];
  const arr = Array.isArray(val) ? val : [val];
  return arr.map((item) => {
    if (typeof item === 'object' && item !== null)
      return {
        type: item.type || 'available',
        text: item.text || '',
        star: !!item.star,
      };
    if (typeof item === 'boolean')
      return { type: item ? 'available' : 'disable', text: '', star: false };
    if (typeof item === 'string') {
      const s = item.trim().toLowerCase();
      if (ICONS.includes(s)) return { type: s, text: '', star: false };
      return { type: 'text', text: item, star: false };
    }
    return { type: 'text', text: String(item || ''), star: false };
  });
}

function getNormalizedData(vehicleId, optionId) {
  if (!vehicleId) return [];
  const rawValue = props.categoryData?.availability?.[vehicleId]?.[optionId];
  return normalize(rawValue);
}

function shouldMergeAll(optionId) {
  if (vCount.value < 2) return false;

  const dataL = getNormalizedData(selectedLeft.value?.id, optionId);
  const dataR = getNormalizedData(selectedRight.value?.id, optionId);

  const isOnlyText = (data) =>
    data.length > 0 && data.every((item) => item.type === 'text');

  if (!isOnlyText(dataL)) return false;

  const strL = JSON.stringify(dataL);
  const strR = JSON.stringify(dataR);
  if (strL !== strR) return false;

  if (hasMiddle.value) {
    const dataM = getNormalizedData(selectedMiddle.value?.id, optionId);
    if (!isOnlyText(dataM)) return false;
    const strM = JSON.stringify(dataM);
    return strL === strM;
  }

  return true;
}

const toggleVisibleRows = () => {
  visibleRows.value =
    visibleRows.value >= optionsAll.value.length
      ? props.initialVisibleRows
      : optionsAll.value.length;
};
</script>

<template>
  <div :key="categoryData.id" class="car-equipment-details__table">
    <div
      class="car-equipment-details__rows"
      :class="{
        'car-equipment-details__rows--3-cols': hasMiddle,
        'car-equipment-details__rows--2-cols': hasRight && !hasMiddle,
        'car-equipment-details__rows--1-col': !hasRight,
      }"
    >
      <div class="car-equipment-details__row car-equipment-details__row--head">
        <div
          class="car-equipment-details__col car-equipment-details__col--options"
        >
          {{ $t('car.equipment_details_options') }}
        </div>

        <div
          v-if="hasLeft"
          class="car-equipment-details__col car-equipment-details__col--vehicle"
        >
          <Select
            v-if="showSelectors"
            v-model="selectedLeft"
            :options="leftOptions"
            option-label="model"
            class="car-equipment-details__select select-without-borders"
            overlay-class="overlay-without-borders"
          >
            <template #value="{ value }">
              <div v-if="value" class="car-equipment-details__vehicle">
                <NuxtImg
                  v-if="value?.image?.src"
                  class="car-equipment-details__vehicle-image"
                  :src="value?.image?.src"
                  :alt="value?.image?.alt"
                  :height="value?.image?.height"
                  :width="value?.image?.width"
                  loading="lazy"
                />
                <span
                  class="car-equipment-details__vehicle-title"
                  v-html="value.model"
                ></span>
              </div>
            </template>
          </Select>
          <div v-else class="car-equipment-details__vehicle">
            <NuxtImg
              v-if="selectedLeft?.image?.src"
              class="car-equipment-details__vehicle-image"
              :src="selectedLeft?.image?.src"
              :alt="selectedLeft?.image?.alt"
              :height="selectedLeft?.image?.height"
              :width="selectedLeft?.image?.width"
              loading="lazy"
            />
            <span
              class="car-equipment-details__vehicle-title"
              v-html="selectedLeft.model"
            ></span>
          </div>
        </div>

        <div
          v-if="hasMiddle"
          class="car-equipment-details__col car-equipment-details__col--vehicle"
        >
          <Select
            v-if="showSelectors"
            v-model="selectedMiddle"
            :options="middleOptions"
            option-label="model"
            class="car-equipment-details__select select-without-borders"
            overlay-class="overlay-without-borders"
          >
            <template #value="{ value }">
              <div v-if="value" class="car-equipment-details__vehicle">
                <NuxtImg
                  v-if="value?.image?.src"
                  class="car-equipment-details__vehicle-image"
                  :src="value?.image?.src"
                  :alt="value?.image?.alt"
                  :height="value?.image?.height"
                  :width="value?.image?.width"
                  loading="lazy"
                />
                <span
                  class="car-equipment-details__vehicle-title"
                  v-html="value.model"
                ></span>
              </div>
            </template>
          </Select>
          <div v-else class="car-equipment-details__vehicle">
            <NuxtImg
              v-if="selectedMiddle?.image?.src"
              class="car-equipment-details__vehicle-image"
              :src="selectedMiddle?.image?.src"
              :alt="selectedMiddle?.image?.alt"
              :height="selectedMiddle?.image?.height"
              :width="selectedMiddle?.image?.width"
              loading="lazy"
            />
            <span
              class="car-equipment-details__vehicle-title"
              v-html="selectedMiddle.model"
            ></span>
          </div>
        </div>

        <div
          v-if="hasRight"
          class="car-equipment-details__col car-equipment-details__col--vehicle"
        >
          <Select
            v-if="showSelectors"
            v-model="selectedRight"
            :options="rightOptions"
            option-label="model"
            class="car-equipment-details__select select-without-borders"
            overlay-class="overlay-without-borders"
          >
            <template #value="{ value }">
              <div v-if="value" class="car-equipment-details__vehicle">
                <NuxtImg
                  v-if="value?.image?.src"
                  class="car-equipment-details__vehicle-image"
                  :src="value?.image?.src"
                  :alt="value?.image?.alt"
                  :height="value?.image?.height"
                  :width="value?.image?.width"
                  loading="lazy"
                />
                <span
                  class="car-equipment-details__vehicle-title"
                  v-html="value.model"
                ></span>
              </div>
            </template>
          </Select>
          <div v-else class="car-equipment-details__vehicle">
            <NuxtImg
              v-if="selectedRight?.image?.src"
              class="car-equipment-details__vehicle-image"
              :src="selectedRight?.image?.src"
              :alt="selectedRight?.image?.alt"
              :height="selectedRight?.image?.height"
              :width="selectedRight?.image?.width"
              loading="lazy"
            />
            <span
              class="car-equipment-details__vehicle-title"
              v-html="selectedRight.model"
            ></span>
          </div>
        </div>
      </div>

      <template v-if="optionsAll.length">
        <div
          v-for="opt in optionsVisible"
          :key="opt.id"
          class="car-equipment-details__row"
        >
          <div
            class="car-equipment-details__col car-equipment-details__col--options"
          >
            <span class="option-name" v-html="opt.title"></span>
          </div>

          <div
            v-if="shouldMergeAll(opt.id)"
            class="car-equipment-details__col car-equipment-details__col--vehicle car-equipment-details__col--merged"
            :style="{ gridColumn: hasMiddle ? 'span 3' : 'span 2' }"
          >
            <div class="car-equipment-details__cell-content">
              <template
                v-for="(item, idx) in getNormalizedData(
                  selectedLeft?.id,
                  opt.id,
                )"
                :key="idx"
              >
                <div class="car-equipment-details__item">
                  <CIcon
                    v-if="item.type !== 'text'"
                    :name="item.type"
                    width="32"
                    height="32"
                    role="img"
                  />
                  <span v-if="item.text" class="body-2-regular">{{
                    item.text
                  }}</span>
                  <span v-if="item.star" class="star">*</span>
                </div>
                <span
                  v-if="
                    idx < getNormalizedData(selectedLeft?.id, opt.id).length - 1
                  "
                  class="car-equipment-details__separator"
                  >/</span
                >
              </template>
            </div>
          </div>

          <template v-else>
            <div
              v-if="hasLeft"
              class="car-equipment-details__col car-equipment-details__col--vehicle"
            >
              <div class="car-equipment-details__cell-content">
                <template
                  v-for="(item, idx) in getNormalizedData(
                    selectedLeft?.id,
                    opt.id,
                  )"
                  :key="idx"
                >
                  <div class="car-equipment-details__item">
                    <CIcon
                      v-if="item.type !== 'text'"
                      :name="item.type"
                      width="32"
                      height="32"
                      role="img"
                    />
                    <span v-if="item.text" class="body-2-regular">{{
                      item.text
                    }}</span>
                  </div>
                  <span
                    v-if="
                      idx <
                      getNormalizedData(selectedLeft?.id, opt.id).length - 1
                    "
                    class="car-equipment-details__separator"
                    >/</span
                  >
                </template>
              </div>
            </div>
            <div
              v-if="hasMiddle"
              class="car-equipment-details__col car-equipment-details__col--vehicle"
            >
              <div class="car-equipment-details__cell-content">
                <template
                  v-for="(item, idx) in getNormalizedData(
                    selectedMiddle?.id,
                    opt.id,
                  )"
                  :key="idx"
                >
                  <div class="car-equipment-details__item">
                    <CIcon
                      v-if="item.type !== 'text'"
                      :name="item.type"
                      width="32"
                      height="32"
                      role="img"
                    />
                    <span v-if="item.text" class="body-2-regular">{{
                      item.text
                    }}</span>
                  </div>
                  <span
                    v-if="
                      idx <
                      getNormalizedData(selectedMiddle?.id, opt.id).length - 1
                    "
                    class="car-equipment-details__separator"
                    >/</span
                  >
                </template>
              </div>
            </div>
            <div
              v-if="hasRight"
              class="car-equipment-details__col car-equipment-details__col--vehicle"
            >
              <div class="car-equipment-details__cell-content">
                <template
                  v-for="(item, idx) in getNormalizedData(
                    selectedRight?.id,
                    opt.id,
                  )"
                  :key="idx"
                >
                  <div class="car-equipment-details__item">
                    <CIcon
                      v-if="item.type !== 'text'"
                      :name="item.type"
                      width="32"
                      height="32"
                      role="img"
                    />
                    <span v-if="item.text" class="body-2-regular">{{
                      item.text
                    }}</span>
                  </div>
                  <span
                    v-if="
                      idx <
                      getNormalizedData(selectedRight?.id, opt.id).length - 1
                    "
                    class="car-equipment-details__separator"
                    >/</span
                  >
                </template>
              </div>
            </div>
          </template>
        </div>
      </template>
    </div>
    <div
      v-if="
        optionsAll.length > initialVisibleRows &&
        visibleRows === initialVisibleRows
      "
      class="car-equipment-details__actions"
    >
      <Button
        class="car-equipment-details__actions-btn button-transparent js-button-equipment"
        rounded
        :variant="'outlined'"
        @click="toggleVisibleRows"
      >
        {{ $t('car.equipment_details_show_more') }}
      </Button>
    </div>
  </div>
</template>
<style scoped lang="scss">
.car-equipment-details {
  $parent: &;
  &__cell-content {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 4);
    }
  }
  &__separator {
    @include body-2-regular;
    line-height: 1;
  }
  &__item {
    display: flex;
    align-items: center;
    gap: 4px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 4);
    }
  }
  &__table {
    display: flex;
    flex-direction: column;
    gap: 40px;
    @include media-breakpoint-up(sm) {
      gap: 56px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 56);
    }
  }
  &__rows {
    background-color: $white;
    border: 1px solid $divider;
    display: grid;
    grid-auto-rows: minmax(51px, auto);
    overflow: hidden;
    width: 100%;
    @include media-breakpoint-up(sm) {
      border-radius: 4px;
      grid-auto-rows: minmax(127px, auto);
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 4);
      @include responsive-spacing(border-width, 1);
      grid-auto-rows: minmax(px-to-vw(127), auto);
    }
    &--1-col {
      #{$parent} {
        &__row {
          grid-template-columns: 1fr;
          @include media-breakpoint-up(sm) {
            grid-template-columns: 4fr 6fr;
          }
        }
      }
    }
    &--2-cols {
      #{$parent} {
        &__row {
          grid-template-columns: 1fr 1fr;
          @include media-breakpoint-up(sm) {
            grid-template-columns: 1fr 1fr 1fr;
          }
        }
      }
    }
    &--3-cols {
      #{$parent} {
        &__row {
          grid-template-columns: 1fr 1fr 1fr;
          @include media-breakpoint-up(sm) {
            grid-template-columns: 1fr 1fr 1fr 1fr;
          }
        }
        &__vehicle {
          @include media-breakpoint-up(md) {
            justify-items: center;
            grid-template-columns: 1fr;
            padding: 0;
          }
          @include media-breakpoint-up(xxl) {
            @include responsive-spacing(padding, 16);
            @include responsive-spacing(gap, 16);
            grid-template-columns: px-to-vw(80) auto;
          }
        }
      }
    }
  }
  &__row {
    align-items: stretch;
    display: grid;
    grid-template-columns: 1fr 1fr;
    min-height: 51px;
    @include media-breakpoint-up(sm) {
      grid-template-columns: repeat(3, 1fr);
    }
    @include media-breakpoint-up(md) {
      min-height: 128px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(min-height, 128);
    }

    &:first-child {
      #{$parent} {
        &__col {
          border-top: unset;
          &--vehicle {
            justify-content: center;
            padding: 0;
          }
        }
      }
    }
    &--head {
      #{$parent} {
        &__col--options {
          @include h6-desktop;
          display: none;
          @include media-breakpoint-up(sm) {
            display: flex;
          }
        }
      }
    }
    &--empty {
      min-height: 51px;
      @include media-breakpoint-up(sm) {
        min-height: 127px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(min-height, 127);
      }
    }
    &--one-vehicle {
      grid-template-columns: 1fr;
      @include media-breakpoint-up(sm) {
        grid-template-columns: repeat(2, 1fr);
      }
    }
  }
  &__col {
    border-top: 1px solid $divider;
    align-items: center;
    display: flex;
    padding: 16px 16px 15px;
    justify-content: center;
    text-align: center;
    border-right: 1px solid $divider;
    &:first-child,
    &:last-child {
      border-right: unset;
    }
    @include media-breakpoint-up(sm) {
      text-align: left;
      padding: 24px;
      &:first-child {
        border-right: 1px solid $divider;
      }
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-width, 1);
      @include responsive-spacing(padding, 24);
    }
    &--options {
      @include body-2-regular;
      grid-column: 1 / -1;
      @include media-breakpoint-up(sm) {
        grid-column: auto;
        justify-content: flex-start;
      }
    }
    &--vehicle-merged {
      grid-column: 1 / -1;
      @include media-breakpoint-up(sm) {
        grid-column: 2 / 4;
      }
    }
  }
  &__select {
    height: 100%;
    padding: 16px;
    width: 100%;
    @include media-breakpoint-up(sm) {
      padding: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding, 24);
    }
    &-image {
      height: auto;
      width: 100%;
    }
    &-title {
      white-space: normal;
    }
  }
  &__actions {
    align-items: start;
    display: flex;
    justify-content: center;
    &-btn {
      @include link-normal;
    }
  }
  &__vehicle {
    align-items: center;
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    justify-items: center;
    padding: 16px;
    @include media-breakpoint-up(sm) {
      gap: 16px;
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: 80px auto;
      justify-items: start;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding, 16);
      @include responsive-spacing(gap, 16);
      grid-template-columns: px-to-vw(80) auto;
    }
    &-title {
      @include vehicle-title;
      white-space: normal;
    }
    &-image {
      height: auto;
      width: 80px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(width, 80);
      }
    }
  }
  &__availability-text {
    @include body-2-regular;
    text-align: center;
    @include media-breakpoint-up(sm) {
      text-align: left;
    }
  }
}
</style>
